/**
 * useMessagesBackTarget — tracks the last non-messages URL the user
 * visited, so tapping the header back button on ANY message page
 * always exits the messaging surface in one tap — no matter how many
 * times the user switched inboxes, opened threads, or drilled around.
 *
 * Persists to `localStorage` so the target survives tab close and cold
 * cold-launches (e.g. tapping an iOS push notification the next day
 * still lands the user back on their actual last "real" screen, not a
 * generic fallback). Falls back to `/profile` when nothing is stored.
 *
 * Mount `useMessagesBackTracker()` once, high up in the tree (App
 * routes level). Call `exitMessages(navigate)` from any message
 * page's back button.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const KEY = 'dl_msg_back_target';

// Any pathname the messaging surface knows about. Landing on these
// should NOT be treated as a "safe exit destination" — otherwise a
// user tabbing between /messages and /roster/messages would set the
// exit target to another message route.
const isMessageRoute = (pathname) => (
  pathname === '/messages'
  || pathname.startsWith('/roster/messages')
  || pathname.startsWith('/athlete/messages')
  || pathname.startsWith('/inbox')
);

// Routes we should never exit *back to* (login, OAuth callbacks,
// share redirects — they either force a redirect on mount or aren't
// useful "home" destinations).
const isSafeExitRoute = (pathname) => (
  pathname
  && pathname !== '/login'
  && !pathname.startsWith('/auth/')
  && !pathname.startsWith('/api/')
  && !isMessageRoute(pathname)
);

export const useMessagesBackTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const full = `${location.pathname}${location.search || ''}`;
    if (isSafeExitRoute(location.pathname)) {
      try { localStorage.setItem(KEY, full); } catch { /* ignore */ }
    }
  }, [location.pathname, location.search]);
};

/**
 * Reads the last-known safe exit URL and navigates the user there.
 * Falls back to `/profile` when nothing is stored (fresh session with
 * no prior in-app navigation — e.g. cold-launch from a push straight
 * into a message thread with an empty localStorage).
 */
export const exitMessages = (navigate) => {
  let target = '';
  try { target = localStorage.getItem(KEY) || ''; } catch { /* ignore */ }
  navigate(target || '/profile');
};
